import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import { formatDateTime } from '@/lib/utils';
import {
  exportCsv,
  formatDuration,
  metersToKm,
  ReportFilters,
  ReportShell,
  ReportTable,
  ResultBar,
  useReportFilters,
} from './_shared';

export function SummaryReportPage() {
  const devices = useFleetStore((s) => s.devices);
  const { deviceId, setDeviceId, preset, setPreset, executed, generate } = useReportFilters('multi');

  const query = useQuery({
    queryKey: ['report', 'summary', executed],
    queryFn: () =>
      reportsApi.summary({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
      }),
    enabled: executed !== null,
  });

  const rows = useMemo(() => query.data ?? [], [query.data]);

  const handleExport = () => {
    if (!executed) return;
    exportCsv(
      `sumario_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      ['Máquina', 'Horas trabalhadas', 'Distância (km)', 'Início', 'Fim'],
      rows.map((r) => [
        r.deviceName ?? devices.get(r.deviceId)?.name ?? '',
        r.engineHours != null ? formatDuration(r.engineHours) : '',
        (r.distance / 1000).toFixed(2),
        r.startTime ? formatDateTime(r.startTime) : '',
        r.endTime ? formatDateTime(r.endTime) : '',
      ]),
    );
  };

  return (
    <ReportShell title="Sumário" description="Tempo trabalhado e atividade por máquina no período.">
      <ReportFilters
        deviceMode="multi"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        onGenerate={generate}
        generating={query.isFetching}
      />
      {executed && (
        <ResultBar
          loading={query.isLoading}
          count={rows.length}
          unit="máquinas"
          onExport={handleExport}
        />
      )}
      <ReportTable
        notRun={executed === null}
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
      >
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-semibold">Máquina</th>
              <th className="px-4 py-2 font-semibold">Horas trabalhadas</th>
              <th className="px-4 py-2 font-semibold">Distância</th>
              <th className="px-4 py-2 font-semibold">Início</th>
              <th className="px-4 py-2 font-semibold">Fim</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.deviceId}-${i}`} className="border-b border-border last:border-0 hover:bg-accent/40">
                <td className="px-4 py-2 font-semibold">{r.deviceName}</td>
                <td className="px-4 py-2 font-mono">
                  {r.engineHours != null ? formatDuration(r.engineHours) : '—'}
                </td>
                <td className="px-4 py-2">{metersToKm(r.distance)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                  {r.startTime ? formatDateTime(r.startTime) : '—'}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                  {r.endTime ? formatDateTime(r.endTime) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTable>
    </ReportShell>
  );
}
